import { useState } from 'react';

import { Button } from '@shared/ui/button';
import { DEXIE_DB_NAME } from '@shared/storage';

/**
 * The recovery screen for `useDb`'s failure path (`src/app.hooks.ts`).
 * Before this existed, a failed Dexie open left the app returning `null`
 * forever from `App.tsx` -- a permanent white screen, with no message and
 * no way for the reader to recover -- while the failure was silently
 * reported to `cf-observability` (`db_open_failure`) with nobody able to
 * see it happen. This is the UI half of fixing that; the reporting half
 * landed in Section 16.
 *
 * Storage failure is a normal state on mobile (private browsing, quota
 * exhaustion, OS-level IndexedDB eviction), not an exceptional one -- the
 * recovery action is scoped to deleting exactly the database that failed
 * (`DEXIE_DB_NAME`), not a full cache/kv wipe (that's the About dialog's
 * "Clear Cache", a different, broader action for a different situation).
 */
export function DbOpenError() {
	const [isClearing, setIsClearing] = useState(false);

	const onClearAndRetry = async () => {
		setIsClearing(true);
		try {
			await new Promise<void>((resolve) => {
				const request = indexedDB.deleteDatabase(DEXIE_DB_NAME);
				// Resolve on all three: a corrupted/unopenable database is
				// exactly the case `onblocked` or `onerror` can fire for, and
				// the reload below is the actual recovery either way -- this
				// promise only exists to give the button a moment of visible
				// "Clearing…" state, not to gate correctness on a clean delete.
				request.onsuccess = () => resolve();
				request.onerror = () => resolve();
				request.onblocked = () => resolve();
			});
		} finally {
			window.location.reload();
		}
	};

	return (
		<div
			role="alert"
			className="flex min-h-[50vh] w-full flex-col items-center justify-center gap-4 bg-background px-6 py-10 text-center text-foreground"
		>
			<div className="space-y-2">
				<h2 className="text-lg font-semibold">
					We couldn&apos;t open local storage
				</h2>
				<p className="max-w-sm text-sm text-muted-foreground">
					This can happen in private browsing, when storage space is
					low, or after an app update. Clearing local data usually
					fixes it -- your account and subscription aren&apos;t
					affected.
				</p>
			</div>
			<Button
				onClick={() => void onClearAndRetry()}
				disabled={isClearing}
			>
				{isClearing ? 'Clearing…' : 'Clear data & retry'}
			</Button>
		</div>
	);
}
